import { CircularProgress, Stack, Tooltip } from "@mui/material";
import { Button } from "@platform/design-system";
import React from "react";

import type { SubmissionStatus } from "./api";

interface FooterButtonsProps {
  status: SubmissionStatus;
  canSubmit: boolean;
  isLastStep: boolean;
  missingFields: string[];
  isOfflineMode: boolean;
  isEditing?: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

function buildMissingFieldsTooltip(missingFields: string[]): string {
  if (missingFields.length === 0) return "";
  return `Missing required fields: ${missingFields.join(", ")}`;
}

/**
 * Builds a user-facing tooltip explaining why submit is disabled.
 */
function buildSubmitTooltip(
  isLastStep: boolean,
  missingFields: string[],
  isEditing: boolean,
): string {
  if (isEditing) return "You must save your changes before submitting";
  if (!isLastStep) return "";
  return buildMissingFieldsTooltip(missingFields);
}

export function FooterButtons({
  status,
  canSubmit,
  isLastStep,
  missingFields,
  isOfflineMode,
  isEditing = false,
  onClose,
  onSubmit,
}: FooterButtonsProps): React.JSX.Element {
  const isSubmitDisabled =
    isEditing || (isOfflineMode ? !canSubmit : !isLastStep || !canSubmit);
  const tooltipTitle = isEditing
    ? buildSubmitTooltip(isLastStep, missingFields, true)
    : isOfflineMode
      ? buildMissingFieldsTooltip(missingFields)
      : buildSubmitTooltip(isLastStep, missingFields, false);

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ justifyContent: "flex-end", px: 3, pb: 3, pt: 2 }}
    >
      {status === "idle" && (
        <>
          <Button variant="tertiary" onClick={onClose}>
            Close
          </Button>
          <Tooltip title={tooltipTitle} arrow placement="top">
            <span>
              <Button
                variant="primary"
                disabled={isSubmitDisabled}
                onClick={onSubmit}
              >
                Submit
              </Button>
            </span>
          </Tooltip>
        </>
      )}
      {(status === "submitting" || status === "exporting") && (
        <Button
          variant="primary"
          disabled
          startIcon={<CircularProgress size={16} color="inherit" />}
        >
          Submit
        </Button>
      )}
      {status === "success" && (
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      )}
    </Stack>
  );
}
